import { useEffect, useState } from 'react';
import { Badge, Box, Button, HStack, Icon, Spinner, Stack, Text } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { LuExternalLink, LuPlus, LuReceipt } from 'react-icons/lu';
import { listClientContracts, type ClientContractSummary, type ContractStatus } from '@/lib/api';
import { fmtDate, fmtMoney, fmtPct } from '@/utils/format';

const MAX_INLINE = 5;

const STATUS_LABEL: Record<ContractStatus, string> = {
  draft: 'Borrador',
  active: 'Activo',
  completed: 'Completado',
  cancelled: 'Cancelado',
};

export function ContractSummaryPanel({
  clientId,
  projectId,
  cached,
  onLoaded,
}: {
  clientId: string;
  projectId: string;
  cached?: ClientContractSummary[];
  onLoaded?: (rows: ClientContractSummary[]) => void;
}) {
  const [rows, setRows] = useState<ClientContractSummary[] | null>(cached ?? null);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cached) {
      setRows(cached);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    listClientContracts(clientId, projectId)
      .then((data) => {
        if (cancelled) return;
        setRows(data);
        onLoaded?.(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'No se pudo cargar el contrato');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, projectId]);

  if (loading) {
    return (
      <HStack gap="2" color="fg.muted" py="2">
        <Spinner size="sm" />
        <Text fontSize="sm">Cargando contrato…</Text>
      </HStack>
    );
  }
  if (error) {
    return (
      <Text color="red.600" fontSize="sm" py="2">
        {error}
      </Text>
    );
  }

  const list = rows ?? [];
  return (
    <Stack gap="3" py="1">
      <HStack justify="space-between" align="center" gap="3" wrap="wrap">
        <Text fontSize="xs" fontWeight="700" color="fg.muted" textTransform="uppercase" letterSpacing="0.06em">
          {list.length === 0 ? 'Sin contrato' : list.length === 1 ? 'Contrato' : `${list.length} contratos`}
        </Text>
        <Button size="xs" variant="outline" asChild>
          <RouterLink to={`/contratos/nuevo?buyerId=${clientId}`}>
            <Icon as={LuPlus} /> Nuevo contrato
          </RouterLink>
        </Button>
      </HStack>

      {list.length === 0 ? (
        <Text fontSize="sm" color="fg.muted">
          Este cliente todavía no tiene un contrato. Crea el primero con el botón "Nuevo contrato".
        </Text>
      ) : (
        <Stack gap="2">
          {list.slice(0, MAX_INLINE).map((ct) => (
            <ContractSummaryRow key={ct.id} ct={ct} />
          ))}
          {list.length > MAX_INLINE && (
            <Button size="xs" variant="ghost" asChild alignSelf="flex-start">
              <RouterLink to={`/contratos?buyerId=${clientId}`}>
                Ver todos en Contratos ({list.length})
              </RouterLink>
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}

function ContractSummaryRow({ ct }: { ct: ClientContractSummary }) {
  return (
    <Box borderWidth="1px" borderColor="border" rounded="md" p="3" bg="bg.surface">
      <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" gap="3">
        <Stack gap="1" minW={0}>
          <HStack gap="2" wrap="wrap">
            <Text fontWeight="700">{ct.contractNumber}</Text>
            <Badge colorPalette={ct.status === 'active' ? 'green' : 'gray'}>{STATUS_LABEL[ct.status]}</Badge>
          </HStack>
          <Text fontSize="sm" color="fg.muted">
            Financiado {fmtMoney(ct.financedAmount)} · {ct.termMonths} meses · {fmtPct(ct.interestRateAnnual)}
          </Text>
          <HStack gap="4" fontSize="sm" wrap="wrap">
            <Text>
              <Text as="span" color="fg.muted">
                Saldo:{' '}
              </Text>
              {fmtMoney(ct.balancePending)}
            </Text>
            <Text>
              <Text as="span" color="fg.muted">
                Próxima cuota:{' '}
              </Text>
              {ct.nextDueDate ? `${fmtDate(ct.nextDueDate)} · ${fmtMoney(ct.nextDueAmount ?? '0')}` : '—'}
            </Text>
          </HStack>
        </Stack>
        <Stack
          direction={{ base: 'row', md: 'column' }}
          gap="2"
          align={{ base: 'stretch', md: 'flex-end' }}
          wrap="wrap"
        >
          <Button size="xs" variant="outline" asChild>
            <RouterLink to={`/contratos/${ct.id}`}>
              <Icon as={LuExternalLink} /> Ver contrato
            </RouterLink>
          </Button>
          {ct.status === 'active' && (
            <Button size="xs" variant="outline" asChild>
              <RouterLink to={`/contratos/${ct.id}?pago=1`}>
                <Icon as={LuReceipt} /> Registrar pago
              </RouterLink>
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
